import { useCallback } from "react";
import {
  setEconomicActivityListState,
  setSelectedEconomicActivityListState,
  setSortEconomicActivityListState,
  resetEconomicActivityListState,
  setSearchTermState,
  setSelectEconomicActivityListState,
  setUnselectEconimicActivityListState,
  setSelectAllEconomicActivityListsState,

  // OPEN
  setOpenCreateEconomicActivityListFormState,
  setOpenEditEconomicActivityListFormState,
  setOpenEconomicActivityListDialogState,
  setOpenEconomicActivityListImportFileState,
  setUpdateCnaeInListState,
  setRemoveCnaeFromListState,
  // CREATE
  setCreateEconomicActivityListDataState,
  setCreateEconomicActivityListListState,
  setCreateEconomicActivityListNameState,
  setCreateEconomicActivityListCnaeState,
  resetCreateEconomicActivityListCnaeState,
  resetCreateEconomicActivityListState,
  setAddUnitCnaeState,
  setNewCnaeState,
  setOldCnaeState,
  // EDIT
  setSelectEconomicActivityListToEditState,
  setEditEconomicActivityListDataState,
  setEditEconomicActivityListState,
  setEditEconomicActivityListNameState,
  setEditEconomicActivityListCnaeState,
  resetEditEconomicActivityListNameState,
  resetEditEconomicActivityListCnaeState,
  resetEditEconomicActivityListState,
  setUpdateStatusEconomicActivityListState,

  // FILE
  setEconomicActivityListFileDataState,
  setEconomicActivityListFileAnalysisDataState,
  setEconomicActivityListImportModalState,
  setSelectedEconomicActivityColumnState,
  setAddMultipleEconomicActivitiesToListState,
  resetEconomicActivityListFileDataState,
  resetEconomicActivityListFileAnalysisState,
  resetEconomicActivityListSelectedColumnState,
} from "../../application/states/economic-activity-list";
import { useAppDispatch, useAppSelector } from "../../application/states/hooks";
import { useEconomicActivityListApi } from "../../infra/economic-activity-list-api";
import { useLoadingService } from "../loading";
import { IEconomicActivityList } from "../../application/models/IEconomicActivityList";
import { create } from "lodash";
import { executeAlert } from "../../application/states/toast-alert";
import {
  addEconomicActivityListFilterState,
  addPurgeEconomicActivityListFilterState,
  removeEconomicActivityListFilterState,
  removePurgeEconomicActivityListFilterState,
} from "../../application/states/project";
import { validateCnae } from "../../utils/formatters/cnae";
import { cnaeNumerico } from "../../utils/formatters/cnae/cnaeNumerico";

export const useEconomicActivityListService = () => {
  const dispatch = useAppDispatch();
  const { startLoading, stopLoading } = useLoadingService();
  const {
    createEconomicActivityListApi,
    findEconomicActivityListByIdApi,
    findEconomicActivityListsApi,
    editEconomicActivityListApi,
    editStatusEconomicActivityListApi,
  } = useEconomicActivityListApi();
  const economicActivityList = useAppSelector(
    (state) => state.economicActivityList,
  );

  const projectState = useAppSelector((state) => state.project);

  const user = useAppSelector((state) => state.user);

  // OPEN FORMS

  const setOpenEconomicActivityListDialog = useCallback(
    (openDialog: boolean) => {
      dispatch(setOpenEconomicActivityListDialogState(openDialog));
    },
    [dispatch],
  );

  const setOpenCreateEconomicActivityListForm = useCallback(
    (openDialog: boolean) => {
      dispatch(setOpenCreateEconomicActivityListFormState(openDialog));
      if (!openDialog) {
        dispatch(setOpenCreateEconomicActivityListFormState(false));
      }
    },
    [dispatch],
  );

  const setOpenEconomicActivityListImportFile = useCallback(
    (openDialog: boolean) => {
      dispatch(setOpenEconomicActivityListImportFileState(openDialog));
    },
    [dispatch],
  );

  const setOpenEditEconomicActivityListForm = useCallback(
    (openDialog: boolean) => {
      dispatch(setOpenEditEconomicActivityListFormState(openDialog));
    },
    [dispatch],
  );

  // outros métodos relacionados à lista de atividades econômicas, como criação, edição, exclusão, etc.

  const setEconomicActivityLists = useCallback(
    async (createdBy: string) => {
      try {
        startLoading();
        const result = await findEconomicActivityListsApi(createdBy);
        if (!result || result.length === 0) {
          executeAlert({
            type: "info",
            message: "Nenhuma lista de atividades econômicas encontrada.",
          });

          dispatch(resetEconomicActivityListState());
          stopLoading();
          return;
        } else {
          dispatch(
            setEconomicActivityListState({ economicActivityLists: result }),
          );
          stopLoading();
        }
      } catch (error) {
        dispatch(resetEconomicActivityListState());
        stopLoading();
      }
    },
    [startLoading, findEconomicActivityListsApi, dispatch, stopLoading],
  );

  const resetEconomicActivityLists = useCallback(() => {
    dispatch(resetEconomicActivityListState());
  }, [dispatch]);

  const setSearchTerm = useCallback(
    (searchTerm: string) => {
      dispatch(setSearchTermState(searchTerm));
    },
    [dispatch],
  );

  const setCnaeSearchTerm = useCallback(
    (searchTerm: string) => {
      dispatch(setSearchTermState(searchTerm));
    },
    [dispatch],
  );

  const setUpdateCnaeInList = useCallback(
    (data: {
      oldValue: string;
      newValue: string;
      index: number;
      mode: string;
    }) => {
      dispatch(setUpdateCnaeInListState(data));
    },
    [dispatch],
  );

  const setRemoveCnaeFromList = useCallback(
    (data: { value: string; index: number; mode: string }) => {
      dispatch(setRemoveCnaeFromListState(data));
    },
    [dispatch],
  );

  const setSelectList = useCallback(
    (listId: string, purge: boolean) => {
      const selectedList: IEconomicActivityList | undefined =
        economicActivityList.economicActivityLists.find(
          (list: IEconomicActivityList) => list.id === listId,
        );
      if (selectedList) {
        if (purge) {
          dispatch(
            addPurgeEconomicActivityListFilterState({
              economicActivityList: selectedList,
            }),
          );

          dispatch(
            setSelectEconomicActivityListState({
              selectedEconomicActivityList: selectedList,
            }),
          );
        } else {
          dispatch(
            addEconomicActivityListFilterState({
              economicActivityList: selectedList,
            }),
          );

          dispatch(
            setSelectEconomicActivityListState({
              selectedEconomicActivityList: selectedList,
            }),
          );
        }
      }
    },
    [economicActivityList.economicActivityLists, dispatch],
  );

  const setUnselectList = useCallback(
    (listId: string, purge: boolean) => {
      let unselectedList: IEconomicActivityList | undefined;
      if (
        projectState.selectedFilter?.purgeEconomicActivityLists?.find(
          (list: IEconomicActivityList) => list.id === listId,
        )
      ) {
        unselectedList =
          projectState.selectedFilter?.purgeEconomicActivityLists?.find(
            (list: IEconomicActivityList) => list.id === listId,
          );
        dispatch(
          removePurgeEconomicActivityListFilterState({
            economicActivityList: unselectedList!,
          }),
        );
        dispatch(setUnselectEconimicActivityListState(unselectedList!));
      } else {
        unselectedList =
          projectState.selectedFilter?.economicActivityList?.find(
            (list: IEconomicActivityList) => list.id === listId,
          );
        dispatch(
          removeEconomicActivityListFilterState({
            economicActivityList: unselectedList!,
          }),
        );

        dispatch(setUnselectEconimicActivityListState(unselectedList!));
      }
    },
    [
      dispatch,
      projectState.selectedFilter?.economicActivityList,
      projectState.selectedFilter?.purgeEconomicActivityLists,
    ],
  );

  const setSelectAllEconomicActivityLists = useCallback(() => {
    dispatch(setSelectAllEconomicActivityListsState());
  }, [dispatch]);

  // Importação
  const resetEconomicActivityListFileData = useCallback(() => {
    dispatch(resetEconomicActivityListFileDataState());
  }, [dispatch]);

  const resetEconomicActivityListSelectedColumn = useCallback(() => {
    dispatch(resetEconomicActivityListSelectedColumnState());
  }, [dispatch]);

  const resetEconomicActivityListFileAnalysis = useCallback(() => {
    dispatch(resetEconomicActivityListFileAnalysisState());
  }, [dispatch]);

  // Ou um método único que reseta tudo
  const resetEconomicActivityListImportState = useCallback(() => {
    dispatch(resetEconomicActivityListFileDataState());
    dispatch(resetEconomicActivityListSelectedColumnState());
    dispatch(resetEconomicActivityListFileAnalysisState());
  }, [dispatch]);

  const setFile = useCallback(
    (data: {
      name: string;
      size: string;
      columns: string[];
      type: string;
      data: any[];
      mode: string;
    }) => {
      startLoading();

      // Dispatch 1: Salvar dados do arquivo
      dispatch(
        setEconomicActivityListFileDataState({
          fileData: {
            columns: data.columns,
            data: data.data,
            name: data.name,
            size: data.size,
            type: data.type,
          },
        }),
      );

      const economicActivityKey = findEconomicActivityInArray(data.data); // Renomeado de cnaeKey para economicActivityKey

      if (economicActivityKey) {
        const replacedFileData = [];
        for (let i = 0; i < data.data.length; i++) {
          const economicActivity = data.data[i];

          if (typeof economicActivity[economicActivityKey] === "number") {
            const replacedEconomicActivity = String(
              economicActivity[economicActivityKey],
            );
            replacedFileData.push(replacedEconomicActivity);
          }
          if (typeof economicActivity[economicActivityKey] === "string") {
            const replacedEconomicActivity = String(
              economicActivity[economicActivityKey],
            );
            replacedFileData.push(replacedEconomicActivity);
          }
        }

        const filteredList = replacedFileData.filter((item) => {
          return !economicActivityList.createEconomicActivityListForm?.fields?.economicActivities?.includes(
            item,
          );
        });

        const repeatedList = replacedFileData.filter((item) => {
          return economicActivityList.createEconomicActivityListForm?.fields?.economicActivities?.includes(
            item,
          );
        });

        const validValues = filteredList.filter((value: string) => {
          let isValid = validateEconomicActivity(value); // Renomeado de validateCnae para validateEconomicActivity
          if (isValid) {
            return value;
          } else {
            return false;
          }
        });

        // Dispatch 2: Salvar análise do arquivo
        dispatch(
          setEconomicActivityListFileAnalysisDataState({
            hasEconomicActivityKey: true,
            economicActivityKey: economicActivityKey,
            validValues: validValues,
            repeatedList: repeatedList,
            filteredList: filteredList,
            totalValidCount: validValues.length,
            totalRepeatedCount: repeatedList.length,
            totalFilteredCount: filteredList.length,
          }),
        );

        if (validValues.length < 1) {
          if (repeatedList.length > 0) {
            // Dispatch 3: Estado para modal de apenas atividades repetidas
            dispatch(
              setEconomicActivityListImportModalState({
                open: true,
                type: "only_repeated",
                title:
                  "Encontramos apenas atividades já inseridas na sua lista atual.",
                message:
                  "Não encontramos nenhuma coluna com atividades econômicas válidas e novas no arquivo. Selecione um arquivo com atividades válidas.",
                fileName: data.name,
              }),
            );
          } else {
            // Dispatch 4: Estado para modal de nenhuma atividade válida
            dispatch(
              setEconomicActivityListImportModalState({
                open: true,
                type: "no_valid_economic_activities",
                title:
                  "Não encontramos atividades econômicas válidas no seu arquivo!",
                message:
                  "Não encontramos nenhuma coluna com atividades econômicas válidas e novas. Selecione outro arquivo.",
                fileName: data.name,
              }),
            );
          }
        } else {
          // Dispatch 5: Selecionar coluna automaticamente
          dispatch(
            setSelectedEconomicActivityColumnState({
              selectedColumn: economicActivityKey,
              data: data.data,
            }),
          );

          // Dispatch 6: Adicionar atividades válidas à lista
          dispatch(
            setAddMultipleEconomicActivitiesToListState({
              economicActivities: validValues,
              mode: data.mode,
            }),
          );

          // Dispatch 7: Estado para modal de sucesso
          dispatch(
            setEconomicActivityListImportModalState({
              open: true,
              type: "success",
              title: `Arquivo ${data.name} carregado com sucesso!`,
              message: `Selecionamos a coluna ${economicActivityKey} como a coluna que contém as atividades econômicas. Caso esta não seja a coluna correta, selecione a correta no menu indicado.`,
              fileName: data.name,
              selectedColumn: economicActivityKey,
              totalImported: validValues.length,
            }),
          );
        }
      } else {
        // Dispatch 8: Estado para modal de erro (nenhuma coluna com atividade)
        dispatch(
          setEconomicActivityListImportModalState({
            open: true,
            type: "error",
            title:
              "Não encontramos atividades econômicas válidas no seu arquivo!",
            message:
              "Não encontramos nenhuma coluna com atividades econômicas válidas. Selecione outro arquivo.",
            fileName: data.name,
          }),
        );

        // Dispatch 9: Salvar análise do arquivo (sem chave de atividade)
        dispatch(
          setEconomicActivityListFileAnalysisDataState({
            hasEconomicActivityKey: false,
            validValues: [],
            repeatedList: [],
            filteredList: [],
            totalValidCount: 0,
            totalRepeatedCount: 0,
            totalFilteredCount: 0,
          }),
        );
      }

      stopLoading();
    },
    [
      startLoading,
      dispatch,
      economicActivityList.createEconomicActivityListForm?.fields
        ?.economicActivities,
      stopLoading,
    ],
  );

  // auxiliares de validação

  // Função para encontrar a chave que contém atividades econômicas no array de dados
  const findEconomicActivityKeyInArray = (data: any[]): string | null => {
    if (!data || data.length === 0) return null;

    // Pega a primeira linha para analisar as colunas
    const firstRow = data[0];
    const keys = Object.keys(firstRow);

    // Procura por colunas que parecem conter atividades econômicas
    for (const key of keys) {
      const value = firstRow[key];
      const stringValue = String(value).replace(/\D/g, "");

      // Ajuste conforme o formato do CNAE (ex: 7 dígitos)
      if (stringValue.length === 7 && !isNaN(Number(stringValue))) {
        return key;
      }
    }

    return null;
  };

  // Função para validar atividade econômica (CNAE)
  const validateEconomicActivity = (value: string): boolean => {
    const numericValue = value.replace(/\D/g, "");

    // Ajuste conforme a regra de negócio (ex: CNAE tem 7 dígitos)
    return numericValue.length === 7 && !isNaN(Number(numericValue));
  };

  // createForm

  const setCreateEconomicActivityListName = useCallback(
    (data: { name: string }) => {
      dispatch(setCreateEconomicActivityListNameState(data.name));
    },
    [dispatch],
  );

  const setCreateEconomicActivityListCnae = useCallback(
    (data: { cnae: string }) => {
      dispatch(setCreateEconomicActivityListCnaeState(data.cnae));
    },
    [dispatch],
  );

  const setAddUnitCnae = useCallback(
    (data: { cnae: string; mode: string }) => {
      dispatch(setAddUnitCnaeState(data));
      dispatch(resetCreateEconomicActivityListCnaeState());
    },
    [dispatch],
  );

  const resetCreateEconomicActivityList = useCallback(() => {
    dispatch(resetCreateEconomicActivityListState());
  }, [dispatch]);

  const setNewCnae = useCallback(
    (data: { cnae: string; mode: string }) => {
      dispatch(setNewCnaeState(data));
    },
    [dispatch],
  );

  const setOldCnae = useCallback(
    (data: { cnae: string; mode: string }) => {
      dispatch(setOldCnaeState(data));
    },
    [dispatch],
  );

  // editForm

  const setSelectEconomicActivityListToEdit = useCallback(
    (id: string) => {
      const selectedEconomicActivityList =
        economicActivityList.economicActivityLists.find(
          (item) => item.id === id,
        );

      if (selectedEconomicActivityList) {
        dispatch(
          setSelectEconomicActivityListToEditState(
            selectedEconomicActivityList,
          ),
        );
      }
    },
    [dispatch, economicActivityList.economicActivityLists],
  );

  const setEditEconomicActivityList = useCallback(
    (list: string[]) => {
      dispatch(setEditEconomicActivityListState(list));
    },
    [dispatch],
  );

  const setEditEconomicActivityListName = useCallback(
    (data: { name: string }) => {
      dispatch(setEditEconomicActivityListNameState(data.name));
    },
    [dispatch],
  );

  const setEditEconomicActivityListCnae = useCallback(
    (data: { cnae: string }) => {
      dispatch(setEditEconomicActivityListCnaeState(data.cnae));
    },
    [dispatch],
  );

  const resetEditEconomicActivityListName = useCallback(() => {
    dispatch(resetEditEconomicActivityListNameState());
  }, [dispatch]);

  const resetEditEconomicActivityListCnae = useCallback(() => {
    dispatch(resetEditEconomicActivityListCnaeState());
  }, [dispatch]);

  const resetEditEconomicActivityList = useCallback(() => {
    dispatch(resetCreateEconomicActivityListState());
  }, [dispatch]);

  const findEconomicActivityInArray = useCallback((array: any[]) => {
    for (let i = 0; i < array.length; i++) {
      const keys = Object.keys(array[i]);
      for (let k = 0; k < keys.length; k++) {
        if (validateCnae(cnaeNumerico(`${array[i][keys[k]]}`))) {
          return keys[k];
        }
      }
    }
    return null;
  }, []);

  const addMultipleEconomicActivitiesToList = useCallback(
    (data: { economicActivities: string[]; mode: string }) => {
      startLoading();

      dispatch(
        setAddMultipleEconomicActivitiesToListState({
          economicActivities: data.economicActivities,
          mode: data.mode,
        }),
      );

      stopLoading();
    },
    [dispatch, startLoading, stopLoading],
  );

  const resetEconomicActivityListImportModal = useCallback(() => {
    dispatch(
      setEconomicActivityListImportModalState({
        open: false,
        type: null,
        title: "",
        message: "",
        fileName: "",
        selectedColumn: "",
        totalImported: 0,
      }),
    );
  }, [dispatch]);

  const selectColumn = useCallback(
    ({
      selectedColumn,
      data,
      mode,
    }: {
      selectedColumn: string;
      data: any[];
      mode: string;
    }) => {
      startLoading();

      // Dispatch 1: Salvar coluna selecionada
      dispatch(
        setSelectedEconomicActivityColumnState({
          selectedColumn,
          data,
        }),
      );

      // Extrair valores da coluna selecionada
      const columnValues = data.map((row) => String(row[selectedColumn] || ""));

      // Filtrar valores que já não existem na lista
      const filteredList = columnValues.filter((item) => {
        return !economicActivityList.createEconomicActivityListForm?.fields?.economicActivities?.includes(
          item,
        );
      });

      const repeatedList = columnValues.filter((item) => {
        return economicActivityList.createEconomicActivityListForm?.fields?.economicActivities?.includes(
          item,
        );
      });

      // Validar valores (assumindo que você tem uma função de validação)
      const validValues = filteredList.filter((value: string) => {
        return value && value.trim() !== "";
      });

      // Atualizar análise do arquivo com os novos valores
      dispatch(
        setEconomicActivityListFileAnalysisDataState({
          hasEconomicActivityKey: true,
          economicActivityKey: selectedColumn,
          validValues: validValues,
          repeatedList: repeatedList,
          filteredList: filteredList,
          totalValidCount: validValues.length,
          totalRepeatedCount: repeatedList.length,
          totalFilteredCount: filteredList.length,
        }),
      );

      // Adicionar valores válidos à lista
      if (validValues.length > 0) {
        dispatch(
          setAddMultipleEconomicActivitiesToListState({
            economicActivities: validValues,
            mode: mode,
          }),
        );

        // Mostrar modal de sucesso
        dispatch(
          setEconomicActivityListImportModalState({
            open: true,
            type: "success",
            title: `Coluna ${selectedColumn} selecionada com sucesso!`,
            message: `A coluna foi selecionada e os dados foram processados.`,
            fileName: economicActivityList.fileData?.name || "",
            selectedColumn: selectedColumn,
            totalImported: validValues.length,
          }),
        );
      } else {
        // Mostrar modal de aviso se não houver valores válidos
        dispatch(
          setEconomicActivityListImportModalState({
            open: true,
            type: "warning",
            title: "Nenhum valor válido encontrado!",
            message: `Não encontramos valores válidos na coluna ${selectedColumn}.`,
            fileName: economicActivityList.fileData?.name || "",
            selectedColumn: selectedColumn,
            totalImported: 0,
          }),
        );
      }

      stopLoading();
    },
    [
      dispatch,
      startLoading,
      stopLoading,
      economicActivityList.createEconomicActivityListForm?.fields
        ?.economicActivities,
    ],
  );

  // Chamadas de API

  const createEconomicActivityList = useCallback(
    async (data: {
      userId: string;
      name: string;
      economicActivityList?: string[];
    }) => {
      try {
        if (data) {
          const result = await createEconomicActivityListApi({
            createdBy: data.userId,
            name: data.name,
            economicActivityList: data.economicActivityList ?? [],
          });

          if (result && result.id) {
            dispatch(
              executeAlert({
                message: "Lista criada com sucesso!",
                type: "success",
              }),
            );
            setOpenCreateEconomicActivityListForm(false);
            await setEconomicActivityLists(user.id);
            return result;
          } else {
            dispatch(
              executeAlert({
                message: "Erro na criação da lista de CNAEs",
                type: "error",
              }),
            );
            return;
          }
        }
      } catch (error) {
        console.error("Erro ao criar lista:", error);
        dispatch(
          executeAlert({
            message: "Erro na criação da lista de CNAEs",
            type: "error",
          }),
        );
        return;
      }
    },
    [
      createEconomicActivityListApi,
      dispatch,
      setEconomicActivityLists,
      setOpenCreateEconomicActivityListForm,
      user.id,
    ],
  );

  // FIND BY ID
  // const findEconomicActivityListById = useCallback(
  //   async (
  //     id: string
  //   ) => {
  //     try {
  //       if (id) {
  //         const result = await findEconomicActivityListByIdApi(id);

  //         if (result && result.id) {

  //           /// IMPLEMENTATIN
  //         } else {
  //           return;
  //         }
  //       }
  //     } catch (error) {
  //       return;
  //     }
  //   },
  //   [],
  // );

  const findEconomicActivityListById = useCallback(
    async (id: string) => {
      if (!id) return;
      startLoading();
      try {
        const result = await findEconomicActivityListByIdApi(id);

        if (result && result.id) {
          dispatch(
            setSelectedEconomicActivityListState(result),
          );
          return result;
        } else {
          dispatch(
            executeAlert({
              message: "Lista de atividade econômica não encontrada",
              type: "warning",
            }),
          );
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Erro ao buscar lista de atividade econômica";
        dispatch(executeAlert({ message: errorMessage, type: "error" }));
        throw error;
      } finally {
        stopLoading();
      }
    },
    [findEconomicActivityListByIdApi, dispatch, startLoading, stopLoading],
  );

  // EDIT LIST
  const editEconomicActivityList = useCallback(
    async (data: {
      id: string;
      userId: string;
      name: string;
      economicActivityList?: string[];
    }) => {
      try {
        if (data) {
          const result = await editEconomicActivityListApi({
            id: data.id,
            createdBy: data.userId,
            name: data.name,
            economicActivityList: data.economicActivityList ?? [],
          });

          if (result && result.id) {
            dispatch(
              executeAlert({
                message: "Lista atualizada com sucesso!",
                type: "success",
              }),
            );
            setOpenEditEconomicActivityListForm(false);
            await setEconomicActivityLists(user.id);
            return;
          } else {
            dispatch(
              executeAlert({
                message: "Erro na edição da lista de CNAEs",
                type: "error",
              }),
            );
            return;
          }
        }
      } catch (error) {
        dispatch(
          executeAlert({
            message: "Erro na edição da lista de CNAEs",
            type: "error",
          }),
        );
        return;
      }
    },
    [
      dispatch,
      editEconomicActivityListApi,
      setEconomicActivityLists,
      setOpenEditEconomicActivityListForm,
      user.id,
    ],
  );

  // EDIT STATUS
  const editStatusEconomicActivityList = useCallback(
    async (data: { id: string; active: boolean }) => {
      try {
        dispatch(
          setUpdateStatusEconomicActivityListState({
            id: data.id,
            active: data.active,
          }),
        );

        if (data) {
          const result = await editStatusEconomicActivityListApi({
            id: data.id,
            active: data.active,
          });

          if (result && result.id) {
            return;
          } else {
            dispatch(
              executeAlert({
                message: "Erro na edição do status da lista de CNAEs",
                type: "error",
              }),
            );
            return;
          }
        }
      } catch (error) {
        dispatch(
          executeAlert({
            message: "Erro na edição do status da lista de CNAEs",
            type: "error",
          }),
        );
        return;
      }
    },
    [dispatch, editStatusEconomicActivityListApi],
  );

  return {
    // OPEN FORMS
    setOpenEconomicActivityListDialog,
    setOpenCreateEconomicActivityListForm,
    setOpenEditEconomicActivityListForm,
    setOpenEconomicActivityListImportFile,
    resetEconomicActivityLists,

    setSearchTerm,
    setCnaeSearchTerm,
    setSelectList,
    setUnselectList,
    setSelectAllEconomicActivityLists,
    setUpdateCnaeInList,
    setRemoveCnaeFromList,
    setFile,

    // CREATE
    setCreateEconomicActivityListName,
    setCreateEconomicActivityListCnae,
    resetCreateEconomicActivityList,
    setAddUnitCnae,
    setNewCnae,
    setOldCnae,

    // IMPORT
    resetEconomicActivityListFileData,
    resetEconomicActivityListSelectedColumn,
    resetEconomicActivityListFileAnalysis,
    resetEconomicActivityListImportState,
    resetEconomicActivityListImportModal,
    selectColumn,
    addMultipleEconomicActivitiesToList,

    // EDIT
    setSelectEconomicActivityListToEdit,
    setEditEconomicActivityListName,
    setEditEconomicActivityListCnae,
    setEditEconomicActivityList,
    resetEditEconomicActivityListName,
    resetEditEconomicActivityListCnae,
    resetEditEconomicActivityList,

    // chamadas de api
    createEconomicActivityList,
    findEconomicActivityListById,
    setEconomicActivityLists,
    editEconomicActivityList,
    editStatusEconomicActivityList,
  };
};
